/**
 * Pure helpers for the Grouped Assets accessible names. The wording itself (translated, with English defaults) lives in
 * RTUTreeList; this file only decides WHICH name applies to a node and what state it announces.
 */

type NodeLike = { key?: unknown; data?: { id?: unknown; name?: unknown; provider?: unknown } | null };

/** Group rows (provider / field) carry an empty id; RTU rows carry the RTU id. */
export const isRtuNode = (node: NodeLike | null | undefined): boolean => Boolean(node?.data?.id);

/** Group rows keep their label in `data.provider` (see createTreeData); RTU rows are named by `data.name`. */
export const nodeDisplayName = (node: NodeLike): string => {
  const raw = isRtuNode(node) ? node.data?.name : node.data?.provider;
  return raw === null || raw === undefined ? '' : String(raw);
};

/** createTreeData keys a group as JSON ["provider" | "field", ...]; anything else is treated as a provider group. */
export const groupKind = (node: NodeLike): 'provider' | 'field' => {
  try {
    const parsed = JSON.parse(String(node.key));
    return Array.isArray(parsed) && parsed[0] === 'field' ? 'field' : 'provider';
  } catch {
    return 'provider';
  }
};

export type SelectionAriaState = 'true' | 'mixed' | 'false';

/** The checkbox state announced to assistive technology: checked, partially checked ("mixed") or unchecked. */
export const selectionAriaState = (checked: boolean, partialChecked: boolean): SelectionAriaState =>
  checked ? 'true' : partialChecked ? 'mixed' : 'false';

export type SelectionAction = 'selectRtu' | 'deselectRtu' | 'selectGroup' | 'deselectGroup';

/**
 * The name describes the ACTION of the next click, like the flat list's row checkboxes: a checked control offers to
 * deselect, an unchecked or partially checked one offers to select (a click on a partial group selects the whole group).
 */
export const selectionAction = (node: NodeLike, checked: boolean): SelectionAction => {
  if (isRtuNode(node)) {
    return checked ? 'deselectRtu' : 'selectRtu';
  }
  return checked ? 'deselectGroup' : 'selectGroup';
};
